const riskPackage = {
  type: 'risks',
  label: 'Risk Score',
  icon: 'I-Check',
  iconType: 'gray',
}
const tier2Package = {
  type: 'tier2',
  label: 'Money Trail',
  icon: 'I-Check',
  iconType: 'gray',
}
const tier2LabelsPackage = {
  type: 'tier2Labels',
  label: 'Funds Breakdown by Risk Category',
  icon: 'I-Check',
  iconType: 'gray',
}
const tier2EntityPackage = {
  type: 'tier2Entity',
  label: 'Reveal Specific Entities',
  icon: 'I-Check',
  iconType: 'gray',
}
const fullPackage = {
  type: 'full',
  label: 'Full Transaction Ledger',
  icon: 'I-Check',
  iconType: 'gray',
}
const popularText = 'Most Popular'

const billingCycle = [
  { name: 'Yearly', value: 'annual', shortName: 'year' },
  { name: 'Monthly', value: 'month', shortName: 'month' }
];

const createStarterPlan = ({ plan = {} } = {}) => {
  const monthlyQuota = plan?.features?.monthlyQuota

  return {
    ...plan,
    type: 'risks',
    tier: 'starter',
    title: 'Starter',
    subTitle: `Simple daily risk checks`,
    quota: [
      {
        icon: 'I-RiskReport',
        iconType: 'gray',
        label: `<span class="value-highlight">${monthlyQuota}</span> reports / month`,
      },
      {
        icon: 'I-S-Info',
        iconType: 'gray',
        label: `No free data updates per report`,
      },
    ],
    includesPackagesLabel: 'Everything in Free, plus:',
    includesPackages: [riskPackage],
    popularText,
  }
}
const createStandardPlan = ({ plan = {} } = {}) => {
  const monthlyQuota = plan?.features?.monthlyQuota
  const freeUpdatesPerReport = plan?.features?.freeUpdatesPerReport

  return {
    ...plan,
    type: 'tier2',
    tier: 'standard',
    title: 'Standard',
    subTitle: `Enhanced crypto risk discovery`,
    quota: [
      {
        icon: 'I-RiskReport',
        iconType: 'gray',
        label: `<span class="value-highlight">${monthlyQuota}</span> reports / month`,
      },
      {
        icon: 'I-Star',
        iconType: 'warning',
        label: `<span class="value-highlight">${freeUpdatesPerReport}</span> Free data updates per report`,
      },
    ],
    includesPackagesLabel: 'Everything in Starter, plus:',
    includesPackages: [tier2Package, tier2LabelsPackage],
    popularText,
  }
}
const createProPlan = ({ plan = {} } = {}) => {
  return {
    ...plan,
    type: 'full',
    tier: 'pro',
    title: 'Pro',
    quota: [
      {
        icon: 'I-RiskReport',
        iconType: 'gray',
        label: `<span class="value-highlight">Unlimited</span> reports`,
      },
      {
        icon: 'I-Star',
        iconType: 'warning',
        label: `<span class="value-highlight">Unlimited</span> data updates per report`,
      },
    ],
    subTitle: `Unlimited investigation power`,
    includesPackagesLabel: 'Everything in Standard, plus:',
    includesPackages: [tier2EntityPackage, fullPackage],
    popularText,
  }
}

const createPlansList = ({ plans = [] } = {}) => {
  const starterPlan = createStarterPlan({
    plan: plans.find(p => p.tier == 'starter'),
  })
  const standardPlan = createStandardPlan({
    plan: plans.find(p => p.tier == 'standard'),
  })
  const proPlan = createProPlan({
    plan: plans.find(p => p.tier == 'pro'),
  })

  return [starterPlan, standardPlan, proPlan]
}


const formatPlanData = ({
  plan,
  currentPlan,
  selectedCycle,
  tierWeights,
  isLoading = false,
  processingPlanId = null
}) => {
  const { tier, title, subTitle, prices, quota, includesPackages } = plan;

  const userTier = currentPlan?.planTier || 'free';
  const userCycle = currentPlan?.billingCycle === 'annual' ? 'annual' : 'month';
  const isTargetAnnual = selectedCycle === 'annual';

  const currentWeight = tierWeights[userTier] || 0;
  const targetWeight = tierWeights[tier] || 0;
  const isSameTier = tier === userTier;

  let actionStatus;
  if (isSameTier && selectedCycle === userCycle) {
    actionStatus = 'current';
  } else if (targetWeight > currentWeight) {
    actionStatus = 'upgrade';
  } else if (isSameTier) {
    actionStatus = 'switch-cycle';
  } else {
    actionStatus = 'downgrade';
  }

  const activePrice = (isTargetAnnual ? prices?.annual : prices?.monthly) || 0;
  const cycleLabel = billingCycle.find(c => c.value === selectedCycle)?.shortName || 'month';

  return {
    id: tier,
    planName: title,
    planDescription: subTitle,
    price: `€${activePrice} / ${cycleLabel}`,
    totalPrice: `€${activePrice}`,
    priceMonthly: `€${prices?.monthly || 0}`,
    priceAnnually: `€${prices?.annual || 0}`,
    billing: `Billed ${isTargetAnnual ? 'annually' : 'monthly'}`,
    actionStatus,
    weight: targetWeight,
    loading: isLoading && processingPlanId === tier,
    benefits: [
      ...(quota || []).map(q => ({
        label: q.label, icon: q.icon, iconType: q.iconType, isHtml: true
      })),
      ...(includesPackages || []).map(p => ({
        label: p.label, icon: p.icon, iconType: p.iconType, isHtml: false
      }))
    ]
  };
};

const getAnnualSaveDiff = (plans) => {
  const list = Array.isArray(plans) ? plans : (plans ? [plans] : [])
  const proPlan = list.find(p => p.tier === 'pro')
  if (!proPlan?.prices) return 0
  return Math.round(proPlan.prices.monthly * 12 - proPlan.prices.annual)
}

const getAnnualSaveDiffFromPlan = (plan) => {
  if (!plan?.prices) return 0
  const { monthly, annual } = plan.prices
  return Math.round(monthly * 12 - annual)
}

const getPlanOptionsWithProfit = (billingCycleOptions, diff, currencySymbol = '€') => {
  const options = billingCycleOptions || billingCycle
  if (diff <= 0) return options
  return options.map(option =>
    option.value === 'annual'
      ? { ...option, profit: `Save up to ${currencySymbol}${diff}` }
      : option
  )
}

const PLAN_PERMISSIONS = {
  starter: ['address', 'risks'],
  standard: ['address', 'risks', 'tier2'],
  pro: ['address', 'risks', 'uof', 'sof', 'full', 'tier2'],
  free: ['address'],
}

const PLANS_HISTORY_AVAILABLE = ['starter', 'standard', 'pro']

export {
  createPlansList,
  formatPlanData,
  billingCycle,
  getAnnualSaveDiff,
  getAnnualSaveDiffFromPlan,
  getPlanOptionsWithProfit,
  PLAN_PERMISSIONS,
  PLANS_HISTORY_AVAILABLE
}
